import { ApiCore, type AuthToken, type AuthUserCredentials } from 'afpnews-api'
import { FunctionsHttpError } from '@supabase/supabase-js'
import {
  handleFunctionsError,
  supabase,
  setSupabaseSession,
  session,
  signOut as supabaseSignOut
} from './supabase'
import {
  useStorage,
  StorageSerializers,
  useTimeoutFn,
  useDocumentVisibility,
  useOnline
} from '@vueuse/core'
import { ref, watch } from 'vue'
import router from '@/router'
import {
  USER_INFO_STORAGE_KEY,
  REFRESH_RETRY_DELAY,
  REFRESH_TIMEOUT,
  REFRESH_FAILURE_WARNING_THRESHOLD
} from '@/config/deck/constants'
import { isTestUno } from '@/config/testUno'

const online = useOnline()

interface SupabaseSession {
  access_token: string
  refresh_token: string
}

type AfpEnv = 'prod' | 'uat'

// Décidé par la edge function (flag `ai_enabled` du compte), pas par le build. Persisté
// pour être connu dès le boot, avant le premier aller-retour authenticate/refresh-token.
const storedAfpEnv = useStorage<AfpEnv>('apicore-env', 'prod', localStorage)

function resolveApicoreBaseUrl(env: AfpEnv): string {
  return env === 'uat'
    ? import.meta.env.VITE_APICORE_URL_UAT
    : import.meta.env.VITE_APICORE_URL_PROD
}

// ── État du refresh de token ─────────────────────────────────────────────────

// Promesse unique en cours, partagée par tous les appelants concurrents
let pendingRefresh: Promise<void> | null = null
let consecutiveRefreshFailures = 0

const refreshDelay = ref(0)
const { start: startRefreshTimer, stop: stopRefreshTimer } = useTimeoutFn(
  () => runRefresh(),
  refreshDelay,
  { immediate: false }
)

// ── Client API AFP ───────────────────────────────────────────────────────────

async function authWithCredentials(credentials: AuthUserCredentials) {
  const { data, error } = await supabase.functions.invoke('authenticate', {
    body: credentials
  })
  await handleFunctionsError(error)
  if (!data?.afpToken) throw new Error('Authentication to AFP API failed')
  return data as {
    afpToken: AuthToken
    supabaseSession: SupabaseSession
    afpEnv: AfpEnv
  }
}

class ModifiedApiCore extends ApiCore {
  // `baseUrl` est protégé mais relu à chaque requête côté afpnews-api : on peut le
  // changer après coup, une fois l'env AFP du compte connu.
  setBaseUrl(url: string) {
    this.baseUrl = url
  }

  async authenticate(credentials?: AuthUserCredentials) {
    if (credentials) {
      const { afpToken, supabaseSession, afpEnv } =
        await authWithCredentials(credentials)
      this.setBaseUrl(resolveApicoreBaseUrl(afpEnv))
      storedAfpEnv.value = afpEnv
      this.token = afpToken
      await setSupabaseSession(supabaseSession)
      this.emit('tokenChanged', this.token)
      return this.token
    }
    if (pendingRefresh) await pendingRefresh
    if (this.isTokenValid) return this.token!
    // Token expiré (ex. timer en retard à cause du throttling navigateur) → refresh.
    await runRefresh()
    if (this.isTokenValid) return this.token!
    throw new Error('Authentication failed')
  }
}

const apicore = new ModifiedApiCore({
  baseUrl: resolveApicoreBaseUrl(storedAfpEnv.value)
})

// ── Refresh centralisé ───────────────────────────────────────────────────────

// Seul un refus d'auth de l'edge (401/403) déconnecte ; le reste (réseau, 5xx) est transitoire.
function isAuthError(error: unknown): boolean {
  if (!(error instanceof FunctionsHttpError)) return false
  const status = error.context?.status
  return status === 401 || status === 403
}

// Replanifie un refresh après un échec réseau, sans déconnecter l'utilisateur.
function scheduleRetry() {
  refreshDelay.value = REFRESH_RETRY_DELAY
  stopRefreshTimer()
  startRefreshTimer()
}

function runRefresh(): Promise<void> {
  if (pendingRefresh) return pendingRefresh
  if (!apicore.token) return Promise.resolve()
  // Hors-ligne (offline-first) : on garde le token, les watchers visibility/online retenteront.
  if (!online.value) return Promise.resolve()

  const tokenSnapshot = apicore.token
  pendingRefresh = (async () => {
    const { data, error } = await supabase.functions.invoke('refresh-token', {
      body: { token: tokenSnapshot },
      timeout: REFRESH_TIMEOUT
    })
    if (error || !data?.afpToken) {
      if (isAuthError(error)) {
        // Refresh token invalide → déconnexion + login.
        consecutiveRefreshFailures = 0
        apicore.token = undefined
        apicore.emit('tokenChanged', undefined)
      } else {
        // Réseau / serveur indisponible : transitoire, on garde le token et on retente.
        consecutiveRefreshFailures++
        if (consecutiveRefreshFailures === REFRESH_FAILURE_WARNING_THRESHOLD) {
          console.warn(
            `[api] refresh-token: ${consecutiveRefreshFailures} échecs consécutifs (réponse 200 sans token ou erreur réseau)`
          )
        }
        scheduleRetry()
      }
      return
    }
    consecutiveRefreshFailures = 0
    const { afpToken, supabaseSession, afpEnv } = data as {
      afpToken: AuthToken
      supabaseSession: SupabaseSession
      afpEnv: AfpEnv
    }
    apicore.setBaseUrl(resolveApicoreBaseUrl(afpEnv))
    storedAfpEnv.value = afpEnv
    apicore.token = afpToken
    await setSupabaseSession(supabaseSession)
    apicore.emit('tokenChanged', apicore.token)
  })()
    .catch(() => {
      // Exception inattendue (ex. établissement de session) : transitoire, on retente.
      scheduleRetry()
    })
    .finally(() => {
      pendingRefresh = null
    })

  return pendingRefresh
}

// Échéance du JWT Supabase en ms epoch (expires_at en secondes), ou null si aucune session.
function supabaseExpiresAt(): number | null {
  const exp = session.value?.expires_at
  return exp != null ? exp * 1000 : null
}

// Requis si l'un des deux tokens est expiré : runRefresh() renouvelle le couple ensemble.
function needsRefresh(): boolean {
  if (!apicore.token) return false
  if (!apicore.isTokenValid) return true
  const supabaseExp = supabaseExpiresAt()
  return supabaseExp != null && supabaseExp <= Date.now()
}

// Planifie à la plus courte des deux échéances : sinon le JWT Supabase (autoRefreshToken off)
// expire avant le token AFP → écritures de préférences en 401 silencieux.
function scheduleRefresh(token: AuthToken) {
  const supabaseExp = supabaseExpiresAt()
  const deadline =
    supabaseExp != null
      ? Math.min(token.tokenExpires, supabaseExp)
      : token.tokenExpires
  refreshDelay.value = Math.max(0, deadline - Date.now())
  stopRefreshTimer()
  startRefreshTimer()
}

// ── Persistance + cycle de vie du token ──────────────────────────────────────

const storedToken = useStorage(
  'apicore-token',
  apicore.token || null,
  localStorage,
  { serializer: StorageSerializers.object }
)
apicore.token = storedToken.value

apicore.on('tokenChanged', (newToken: AuthToken | undefined) => {
  if (newToken) {
    storedToken.value = newToken
    scheduleRefresh(newToken)
  } else {
    storedToken.value = null
    stopRefreshTimer()
    void router.replace({ name: 'login' })
  }
})

if (apicore.token) {
  if (apicore.isTokenValid) {
    scheduleRefresh(apicore.token)
  } else {
    void runRefresh()
  }
}

// Restauration d'onglet : cas mobile où les timers gelés laissent un JWT Supabase périmé.
const visibility = useDocumentVisibility()
watch(visibility, v => {
  if (v === 'visible' && needsRefresh()) {
    void runRefresh()
  }
})

// Retour de connectivité : retente le refresh ignoré pendant la coupure.
watch(online, isOnline => {
  if (isOnline && needsRefresh()) {
    void runRefresh()
  }
})

// Le JWT Supabase est hydraté en asynchrone (INITIAL_SESSION) : re-planifier pour suivre sa nouvelle échéance.
watch(session, () => {
  if (apicore.token && apicore.isTokenValid) scheduleRefresh(apicore.token)
})

// ── Filtrage des documents de test ──────────────────────────────────────────

// N'affecte que les appelants qui l'utilisent explicitement (ex. les tools IA, via
// wrapApicoreForAi dans lib/ai/mcpClient.ts) — pas appliqué globalement à `apicore`.
// À ne pas brancher tel quel sur un flux paginé (search/searchAll d'actions.ts) : ça
// filtre `documents` sans ajuster `count`, ce qui désynchroniserait la pagination.
export function dropTestDocs<T extends { documents: unknown[] }>(result: T): T {
  return {
    ...result,
    documents: result.documents.filter(
      d => !isTestUno((d as { uno?: unknown }).uno)
    )
  }
}

// ── Exports ──────────────────────────────────────────────────────────────────

export { runRefresh as forceRefreshAfpToken }

export const afpEnv = storedAfpEnv

export async function logout() {
  apicore.token = undefined
  apicore.emit('tokenChanged', undefined)
  localStorage.removeItem(USER_INFO_STORAGE_KEY)
  await supabaseSignOut()
}

export default apicore
